import { Grid, LoadingOverlay, Stack, Title } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';

import { Declarant, EnvironmentCountries } from '@e-origin/shared';

import { AsyncAutocomplete, EucdmCode, MultiLangCodes, TextInput } from '@components';
import { useGoodLocation } from '@hooks/declarant';
import { useCodeListSystem } from '@hooks/useCodeListSystem';
import { useSettings } from '@hooks/useSettings';
import { DeclarationGeneralFormValues } from './validateDeclarationGeneralForm';

interface CustomsSectionProps {
  form: UseFormReturnType<DeclarationGeneralFormValues>;
  editDisabled?: boolean;
  isExport?: boolean;
  declarant?: Declarant;
  declarationGroup?: string;
}

const CUSTOMS_OFFICE_PRESENTATION_REF = 'consignment.customsOfficePresentation.referenceNumber';

export const CustomsSection: React.FC<CustomsSectionProps> = ({ form, editDisabled, isExport, declarant, declarationGroup }) => {
  const { data: settings } = useSettings();
  const isNetherlands = settings?.country === EnvironmentCountries.NL;
  const { data: { data: goodsLocations } = {}, isLoading } = useGoodLocation(declarant?._id);

  const codelistSystems = useCodeListSystem({
    group: declarationGroup,
    isExport,
  });

  if (!goodsLocations) {
    return <LoadingOverlay visible={isLoading} overlayProps={{ radius: 'sm', blur: 2 }} />;
  }
  return (
    <Stack mb="xl">
      <Title order={5} fw={600}>
        Customs
      </Title>

      <Grid>
        {!isNetherlands && (
          <Grid.Col span={6}>
            <EucdmCode path={CUSTOMS_OFFICE_PRESENTATION_REF}>
              <MultiLangCodes
                {...form.getInputProps(CUSTOMS_OFFICE_PRESENTATION_REF)}
                label="Presentation Office"
                code={isExport ? 'CL193' : 'CL141'}
                system={codelistSystems}
                onChange={(selectedOption) => form.setFieldValue(CUSTOMS_OFFICE_PRESENTATION_REF, selectedOption)}
                disabled={editDisabled}
              />
            </EucdmCode>
          </Grid.Col>
        )}
        <Grid.Col span={6}>
          <EucdmCode path="declaration.consignment.locationOfGoods.unLocode">
            <AsyncAutocomplete
              {...form.getInputProps('consignment.locationOfGoods.unLocode')}
              label="UNLOCODE/Warehouse ID"
              onChange={(selectedOption) => {
                form.setFieldValue('consignment.locationOfGoods.unLocode', selectedOption);
              }}
              data={(goodsLocations || []).map((location) => ({
                value: location.unLocode,
                label: location.unLocode,
              }))}
              disabled={editDisabled}
            />
          </EucdmCode>
        </Grid.Col>
      </Grid>

      {isExport && (
        <>
          <Grid>
            <Grid.Col span={6}>
              <TextInput {...form.getInputProps('consignment.customsOfficeExit.referenceNumber')} label="Exit office" disabled={editDisabled} />
            </Grid.Col>
            <Grid.Col span={6}>
              <TextInput {...form.getInputProps('consignment.customsOfficeExport.referenceNumber')} label="Export office" disabled={editDisabled} />
            </Grid.Col>
          </Grid>

          <Grid>
            <Grid.Col span={6}>
              <MultiLangCodes
                {...form.getInputProps('consignment.customsSupervisingOffice.referenceNumber')}
                label="Supervising customs office"
                code="CL141"
                onChange={(selectedOption) => form.setFieldValue('consignment.customsSupervisingOffice.referenceNumber', selectedOption)}
                disabled={editDisabled}
              />
            </Grid.Col>
          </Grid>
        </>
      )}
    </Stack>
  );
};
